// Google Ads conversion events for the direct quote flow (JUNO-3987).
//
// The quote flow is a single-page app: StepFlowNavigate rewrites `step=` with
// the History API and never does a real page load, so Google's URL-rule
// conversions only ever see the entry URL. Each step fires its conversion by
// hand instead, through window.gtag from appConfig.js.
//
// Nothing here is gated on cookie consent. The Ads tag is configured for
// everyone with Consent Mode v2 defaults denied (see appConfig.gohtml); while
// consent is denied the tag sets no cookies and sends cookieless pings that
// Google models. When the Ads tag ID is not configured (preprod, preview,
// local) nothing is sent.
//
// No personal details are ever passed to gtag: the events carry only the
// conversion label and, for the quote and the "Sign up now" click, a value,
// currency and a transaction id. Enhanced conversions (hashed email) are out of scope.

// Conversion labels from the Google Ads account (JUNO-3975), keyed by the
// step slug in QuoteFlow.js, plus "Sign up now" on the quote. The account's
// tag ID is prepended at send time.
export const ADS_CONVERSION_LABELS = {
  "buying-quote": "1GQ5CNOPuO4cEKXL8oIp",
  "selling-quote": "Q2SECNaPuO4cEKXL8oIp",
  "personal-details": "gP-rCJWIse4cEKXL8oIp",
  "buying-sorry": "P-VpCN3SuO4cEKXL8oIp",
  "selling-sorry": "_00ZCODSuO4cEKXL8oIp",
  "your-quote": "VgHnCN6Pt-4cEKXL8oIp",
  "signup-started": "17UqCICZ-qwdEKXL8oIp",
};

export const ADS_CONVERSION_CURRENCY = "GBP";

const adsTagId = () => window.appConfig?.google_ads_tag_id || null;

// Fires the conversion for a quote step or the sign-up click. Returns true when an event was sent,
// false when the step has no label, the Ads tag is not configured, or gtag is
// not loaded (ad blockers, tests). Never throws: analytics must not be able
// to take down a quote step.
export function fireAdsConversion(step, extra = {}) {
  const label = ADS_CONVERSION_LABELS[step];
  const tagId = adsTagId();
  if (!label || !tagId || typeof window.gtag !== "function") {
    return false;
  }
  try {
    window.gtag("event", "conversion", { send_to: `${tagId}/${label}`, ...extra });
  } catch (err) {
    console.error("Ads conversion failed:", err);
    return false;
  }
  return true;
}

// A conversion value from the quote API (JUNO-3976, app/adsconversion), or 0
// when the API sends none or something that isn't a positive number.
export function adsValue(raw) {
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

// The quote API sends a transaction id derived from the quote's id, so a
// later upload can restate the conversion's value. Should it be missing,
// each quote gets a random one, so Google never collapses two quotes.
export function adsTransactionId(quoteResponse) {
  const id = quoteResponse?.adsTransactionId;
  return typeof id === "string" && id !== "" ? id : newAdsTransactionId();
}

export function newAdsTransactionId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

// The primary conversion: a quote was shown and a lead created.
export function fireQuoteShownConversion(quoteResponse) {
  return fireAdsConversion("your-quote", {
    value: adsValue(quoteResponse?.adsConversionValue),
    currency: ADS_CONVERSION_CURRENCY,
    transaction_id: adsTransactionId(quoteResponse),
  });
}

// The sign-up started conversion, when "Sign up now" is clicked on the quote.
// Its value is what the click adds to the quote's (JUNO-3976). The link then
// leaves the page, which is fine: gtag.js sends Ads hits with fetch keepalive,
// which outlives the page.
export function fireSignupStartedConversion(quoteResponse) {
  return fireAdsConversion("signup-started", {
    value: adsValue(quoteResponse?.adsSignupValue),
    currency: ADS_CONVERSION_CURRENCY,
    transaction_id: adsTransactionId(quoteResponse),
  });
}
